import { useEffect, useState, useCallback } from "react";
import api from "../../../../../features/auth/axiosConfig";
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from "date-fns";
import { OrderData } from "../types/dashboardTypes";

export const useOrders = (timeRange: string, customStartDate: string, customEndDate: string) => {
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const today = new Date();
      let from = "";
      let to = "";

      switch (timeRange) {
        case "today":
          from = format(today, "yyyy-MM-dd");
          to = format(today, "yyyy-MM-dd");
          break;
        case "yesterday": {
          const y = subDays(today, 1);
          from = format(y, "yyyy-MM-dd");
          to = format(y, "yyyy-MM-dd");
          break;
        }
        case "week":
          from = format(startOfWeek(today), "yyyy-MM-dd");
          to = format(endOfWeek(today), "yyyy-MM-dd");
          break;
        case "month":
          from = format(startOfMonth(today), "yyyy-MM-dd");
          to = format(endOfMonth(today), "yyyy-MM-dd");
          break;
        case "year":
          from = format(startOfYear(today), "yyyy-MM-dd");
          to = format(endOfYear(today), "yyyy-MM-dd");
          break;
        case "custom":
          if (customStartDate && customEndDate) {
            from = customStartDate;
            to = customEndDate;
          } else {
            // Don't fetch if custom range is not fully specified
            setLoading(false);
            return;
          }
          break;
        default:
          from = format(today, "yyyy-MM-dd");
          to = format(today, "yyyy-MM-dd");
          break;
      }

      const pageNumber = 1;
      const pageSize = 1000;
      const url = `/app/pos/v2/order?pageNumber=${pageNumber}&pageSize=${pageSize}&dateFilter=custom&startDate=${from}&endDate=${to}&searchBy=&staff_name=`;

      const response = await api.get(url);
      const result = response?.data?.result ?? response?.data ?? [];

      setOrders(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("Failed to fetch orders");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [timeRange, customStartDate, customEndDate]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return { orders, loading, error, refetch: fetchOrders };
};
